/**
 * A step that is the customer's move (kanban d3915b86, Jess 2026-10-21).
 *
 * Quality never contacts a customer from the door: "our chain of command
 * goes: I go through our sales reps." So a customer-owed item carries no
 * Ask. It carries a RECOMMENDATION of how it reaches the customer — through
 * the account's sales rep, pre-named when the account has one — which Jess
 * rewords on the case and Merlin rewords from chat. Nothing is sent for it.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { DoorAskPersonRead } from '../../services/qcpDataRequests';
import type { DoorCaseTaskRead } from '../../services/qcpInvestigations';

/** Whether the row shows a recommendation instead of an Ask. */
export function isCustomerStep(task: Pick<DoorCaseTaskRead, 'owner'>): boolean {
  return task.owner === 'customer';
}

/**
 * What the row says the customer step should become: the editor's own
 * wording when there is one, else the default naming the account's sales
 * rep ("Contact the customer through Cliff Platt, the account's sales rep"),
 * or the chain itself when the account has no rep on file.
 */
export function customerRecommendation(
  task: Pick<DoorCaseTaskRead, 'recommendation'>,
  rep: DoorAskPersonRead | null | undefined,
): string {
  const own = (task.recommendation ?? '').trim();
  if (own) return own;
  return defaultCustomerRecommendation(rep);
}

/** The default wording, as the editor sees it before rewording. */
export function defaultCustomerRecommendation(rep: DoorAskPersonRead | null | undefined): string {
  const name = (rep?.name ?? '').trim();
  return name
    ? `Contact the customer through ${name}, the account's sales rep`
    : "Contact the customer through the account's sales rep";
}

/** True when the row shows its own wording rather than the default. */
export function hasOwnRecommendation(task: Pick<DoorCaseTaskRead, 'recommendation'>): boolean {
  return (task.recommendation ?? '').trim().length > 0;
}

/** Why no Ask sits on the row — the hint on the recommendation line. */
export const CUSTOMER_STEP_HINT =
  'Quality does not contact customers from here: the account\'s sales rep does. ' +
  'Nothing is sent for this step; reword the recommendation (or ask Merlin to) when the plan changes.';
